import { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useSelector, useDispatch } from "react-redux";
import { Grid } from "@arco-design/web-react";
import { Form, Input, Button, Message, Typography, Link, Divider } from "@arco-design/web-react";
import { IconEmail } from "@arco-design/web-react/icon";
import axios from "axios";

import { endpoints } from "../../api/endpoints";
import { auth } from "../../firebaseConfig";
import { Logo } from "../../components/Logo";
import { GoogleRegisterButton } from "../../components/GoogleRegisterButton";
import { MicrosoftRegisterButton } from "../../components/MicrosoftRegisterButton";
import { LanguageSelect } from "../../components/LanguageSelect/LanguageSelect";
import { getBoxBackgroundColor, getButtonColor, getButtonTextColor, getColorAccent1, getTextColor } from "../../utils/cssVariables";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { setUser } from "../../store/userSlice";
import { trackSignUp } from "../../analytics/track";
import { getAuthLogoType } from "../../theme";
import { isNycRegistrationMode, NYC_MENTOR_SURVEY_URL } from "../../constants/nycRegistration";
import { completeAuthRedirect, shouldDeferAutoAuthRedirect } from "../../utils/authReturnUrl";
import { defaultAuthenticatedPath } from "../../utils/defaultAuthenticatedPath";
import { useEventIntakeEnabled } from "../../hooks/useEventIntakeEnabled";
import { isBookMeetingIntent, loginPathWithBookMeetingIntent } from "../../constants/authBookMeeting";
import { isQrRegistrationSource, QR_REGISTRATION_SOURCE } from "../../constants/qrRegistration";
import {
    guestIntakePath,
    registrationAttributionFromSearchParams,
} from "../../constants/guestIntake";
import { BookMeetingAuthModal } from "../../components/BookMeetingAuthModal/BookMeetingAuthModal";
import "./RegisterLegacy.css";
import { isDuplicateEmailSignUpError, isExistingFirebaseSignUpSuccess, resolveSignUpErrorMessage } from "../../utils/signUpError";


const Row = Grid.Row;
const FormItem = Form.Item;

const handleSignUpError = (error: unknown, t: TFunction<'translation', undefined>, onDuplicateEmail?: () => void) => {
    if (isDuplicateEmailSignUpError(error) && onDuplicateEmail) {
        onDuplicateEmail();
        return;
    }

    const message = resolveSignUpErrorMessage(error, t);
    if (!message) {
        console.error("Unknown signup error:", error);
        Message.error(t('notification_messages.unexpected_error'));
        return;
    }

    Message.error(message);
}

export const RegisterLegacy = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user.user);
    const qrSignupEnabled = useSelector((state: RootState) => state.config.qrSignupEnabled);
    const eventIntakeEnabled = useEventIntakeEnabled();
    const brandName = useSelector((state: RootState) => state.config.brandName);
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const [form] = Form.useForm();

    const registrationSource = searchParams.get('source');
    const intent = searchParams.get('intent');
    const isBookMeetingFlow = isBookMeetingIntent(intent);
    const intakeAttribution = registrationAttributionFromSearchParams(searchParams);
    const loginHref = isBookMeetingFlow
        ? loginPathWithBookMeetingIntent(intakeAttribution)
        : isQrRegistrationSource(registrationSource)
            ? `${routes.LOGIN}?source=${QR_REGISTRATION_SOURCE}`
            : routes.LOGIN;
    const isQrRegistrationFlow =
        isQrRegistrationSource(registrationSource) && qrSignupEnabled;

    const [isMentor, setIsMentor] = useState<boolean>(false);
    const [isMentee, setIsMentee] = useState<boolean>(false);
    const [isGuest, setIsGuest] = useState<boolean>(false);

    const [isEmailVerificationSent, setEmailVerificationSent] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState<string>("");
    const [bookMeetingModalOpen, setBookMeetingModalOpen] = useState(isBookMeetingFlow);
    const configLoaded = useSelector((state: RootState) => state.config.configLoaded);

    useEffect(() => {
        if (!user || shouldDeferAutoAuthRedirect() || !configLoaded) {
            return;
        }
        const defaultPage = defaultAuthenticatedPath(user, eventIntakeEnabled, searchParams);
        completeAuthRedirect(defaultPage);
    }, [user, eventIntakeEnabled, searchParams, configLoaded]);

    const goToSignInFromQr = () => {
        navigate(loginHref);
    };

    const redirectDuplicateEmailToLogin = () => {
        Message.info(t('notification_messages.sign_up_error_tenant_duplicate'));
        goToSignInFromQr();
    };

    const redirectReusedFirebaseToLogin = () => {
        Message.info(t('notification_messages.sign_up_existing_firebase_sign_in'));
        goToSignInFromQr();
    };

    // Load reCAPTCHA script when configured (backend skips verification when unset).
    useEffect(() => {
        const sitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (!sitekey) {
            return;
        }

        // Check if script is already loaded
        if (document.querySelector(`script[src*="recaptcha/enterprise.js"]`)) {
            return;
        }

        // Load reCAPTCHA script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${sitekey}`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        return () => {
            // Cleanup script on unmount
            const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, []);

    const handleResendEmail = async () => {
        try {
            if (!registeredEmail) {
                Message.error(t('email_confirmation.confirmation_resent_error'));
                return;
            }

            // The endpoint is unprotected, so no authentication is needed
            await axios.post(
                endpoints.confirmEmail,
                {
                    email: registeredEmail,
                }
            );

            Message.success({ closable: true, content: t('email_confirmation.confirmation_resent_success') });
        } catch (error) {
            console.error(error);
            Message.error(t('email_confirmation.confirmation_resent_error'));
        }
    };

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            const { name, last_name, password } = values;
            const email = String(values.email ?? '').trim();

            // Generate reCAPTCHA token when configured (backend skips when recaptcha client is unset).
            const sitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
            let token = "";
            if (sitekey) {
                const grecaptcha = (window as any).grecaptcha;
                if (!grecaptcha || !grecaptcha.enterprise) {
                    Message.error(t('register_page.recaptcha_not_loaded'));
                    return;
                }

                try {
                    token = await grecaptcha.enterprise.execute(sitekey, { action: 'signup' });
                } catch (error) {
                    console.error('reCAPTCHA execute error:', error);
                    Message.error(t('register_page.captcha_required'));
                    return;
                }

                if (!token) {
                    Message.error(t('register_page.captcha_required'));
                    return;
                }
            }

            const signupPayload: Record<string, unknown> = {
                    name,
                    last_name,
                    password,
                    email,
                    is_mentor: isGuest ? false : isMentor,
                    is_guest: isGuest,
                    is_confirmed: isBookMeetingFlow || isGuest,
                };
            if (token) {
                signupPayload.recaptcha_token = token;
            }
            if (isQrRegistrationFlow) {
                signupPayload.source = QR_REGISTRATION_SOURCE;
            } else if (intakeAttribution.source) {
                signupPayload.source = intakeAttribution.source;
            }

            const signupResponse = await axios.post(
                endpoints.signupWithEmail,
                signupPayload
            );

            if (isGuest && isExistingFirebaseSignUpSuccess(signupResponse.data)) {
                redirectReusedFirebaseToLogin();
                return;
            }

            trackSignUp({
                method: "email",
                role: isGuest ? "guest" : isMentor ? "mentor" : "mentee",
                source: isQrRegistrationFlow
                    ? QR_REGISTRATION_SOURCE
                    : intakeAttribution.source,
            });

            if (isGuest) {
                try {
                    const userCredential = await signInWithEmailAndPassword(auth, email, password);
                    const idToken = await userCredential.user.getIdToken();
                    const response = await axios.get(endpoints.whoami, {
                        headers: { Authorization: `Bearer ${idToken}` },
                    });
                    dispatch(setUser({ ...response.data }));
                    completeAuthRedirect(guestIntakePath(intakeAttribution));
                } catch (loginError) {
                    console.error('Guest auto-login failed:', loginError);
                    redirectReusedFirebaseToLogin();
                }
                return;
            }

            if (isBookMeetingFlow) {
                try {
                    const userCredential = await signInWithEmailAndPassword(auth, email, password);
                    const idToken = await userCredential.user.getIdToken();
                    const response = await axios.get(endpoints.whoami, {
                        headers: { Authorization: `Bearer ${idToken}` },
                    });
                    dispatch(setUser({ ...response.data }));
                    completeAuthRedirect(routes.DASHBOARD);
                } catch (loginError) {
                    console.error('Book meeting auto-login failed:', loginError);
                    Message.info(t('register_page.account_exists_message'));
                    navigate(loginHref);
                }
                return;
            }

            await axios.post(
                endpoints.confirmEmail,
                {
                    email,
                }
            ).finally(() => {
                setRegisteredEmail(email);
                setEmailVerificationSent(true);
            });
        } catch (error) {
            handleSignUpError(
                error,
                t,
                isQrRegistrationFlow || isGuest ? redirectDuplicateEmailToLogin : undefined
            );
            // No need to reset for score-based keys - they generate a new token on each execute
        }
    };

    const onMentorPick = () => {
        if (isNycRegistrationMode()) {
            window.location.href = NYC_MENTOR_SURVEY_URL;
            return;
        }
        setIsMentor(true);
    };

    const onMenteePick = () => {
        setIsMentee(true);
    };

    const onGuestPick = () => {
        setIsGuest(true);
    };

    const onGoBack = () => {
        setIsMentor(false);
        setIsMentee(false);
        setIsGuest(false);
    };

    const rolePillButtonStyle = {
        color: getButtonTextColor(),
        backgroundColor: getButtonColor(),
        height: 40,
        borderRadius: 20,
    };

    const PageWrapper = ({ children }: { children: ReactNode }) => (
        <div
            style={{
                justifyContent: "center",
                backgroundColor: getBoxBackgroundColor(),
                maxWidth: 480,
                width: "calc(100% - 40px)",
                margin: "20px auto",
                padding: "20px",
                borderRadius: 8,
            }}
        >
            <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
            <div style={{
                    display: 'flex',
                    justifyContent: "center",
                    paddingTop: 12,
                    margin: "0 auto",
                  }}><LanguageSelect color={getTextColor()}/></div>
            <BookMeetingAuthModal
                visible={isBookMeetingFlow && bookMeetingModalOpen}
                brandName={brandName || 'WERULE'}
                onSignIn={() => {
                    setBookMeetingModalOpen(false);
                    navigate(loginHref);
                }}
                onRegister={() => setBookMeetingModalOpen(false)}
                onClose={() => setBookMeetingModalOpen(false)}
            />
            {children}
        </div>
    )

    // useEffect(() => {

    // }, [isEmailVerificationSent])

    if (isQrRegistrationSource(registrationSource) && !qrSignupEnabled) {
        return (
            <PageWrapper>
                <Typography.Title heading={4} style={{ marginTop: 24, textAlign: 'center' }}>
                    Event registration unavailable
                </Typography.Title>
                <Typography.Text style={{ display: 'block', marginTop: 16, textAlign: 'center' }}>
                    QR registration is not enabled for this organization.
                </Typography.Text>
                <Row justify="center" style={{ marginTop: 32 }}>
                    <Button shape="round" size="large" onClick={() => navigate(loginHref)}>
                        {t('register_page.back_to_login')}
                    </Button>
                </Row>
            </PageWrapper>
        );
    }

    if (isEmailVerificationSent) {
        return (
            <PageWrapper>
                <Row
                    justify="center"
                    style={{
                        alignItems: "center",
                        gap: 12,
                        marginTop: 40,
                        marginBottom: 24,
                    }}
                >
                    <IconEmail style={{ fontSize: 32, stroke: getColorAccent1() }} />
                    <Typography.Text
                        style={{
                            fontSize: 20,
                            color: getColorAccent1(),
                            wordBreak: "break-word",
                        }}
                        type="success"
                        className={"alice-regular"}
                    >
                        {t('email_confirmation.email_confirmation')}
                    </Typography.Text>
                </Row>

                <Typography.Text style={{ wordBreak: "break-word" }}>
                    {t('register_page.thanks_for_registration')}
                </Typography.Text>
                <Row
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 24,
                        marginTop: 40,
                    }}
                >
                    <Button
                        onClick={() => {
                            navigate(loginHref)
                        }}
                        size={"large"}
                        shape={"round"}
                    >
                        {t('register_page.back_to_login')}
                    </Button>
                    <Button
                        htmlType="submit"
                        style={{
                            color: getButtonTextColor(),
                            backgroundColor: getButtonColor(),
                        }}
                        size={"large"}
                        shape={"round"}
                        onClick={() => handleResendEmail()}
                    >
                        {t('email_confirmation.resend_confirmation')}
                    </Button>
                </Row>
            </PageWrapper>
        );
    }

    return isMentor || isMentee || isGuest ? (
        <div
            style={{
                justifyContent: "center",
                backgroundColor: getBoxBackgroundColor(),
                maxWidth: 480,
                width: "calc(100% - 40px)",
                margin: "20px auto",
                padding: "20px",
                borderRadius: 8,
            }}
        >
            <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
            <div style={{
                display: 'flex',
                justifyContent: "center",
                paddingTop: 12,
                margin: "0 auto",
            }}><LanguageSelect color={getTextColor()}/></div>
            <div style={{ marginBottom: 16 }}>
                <Typography.Title heading={2}>
                    {t('register_page.register_as')}{' '}
                    {isGuest
                        ? t('common.guest')
                        : isMentee
                            ? t('common.mentee')
                            : t('common.mentor')}
                </Typography.Title>

                <Form
                    form={form}
                    autoComplete="off"
                    size={"default"}
                    scrollToFirstError
                    onSubmit={onSubmit}
                >
                    <div style={{ marginTop: 12 }}>
                        {t('register_page.agreement')}
                        <Link
                            href={"https://we-rule.com/terms-conditions"}
                            target="_blank"
                        >
                            {t('register_page.terms')}
                        </Link>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <GoogleRegisterButton
                            isMentor={isMentor}
                            isGuest={isGuest}
                            signupSource={isQrRegistrationFlow ? QR_REGISTRATION_SOURCE : intakeAttribution.source ?? undefined}
                            redirectPath={isGuest ? guestIntakePath(intakeAttribution) : undefined}
                        />
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <MicrosoftRegisterButton
                            isMentor={isMentor}
                            isGuest={isGuest}
                            signupSource={isQrRegistrationFlow ? QR_REGISTRATION_SOURCE : intakeAttribution.source ?? undefined}
                            redirectPath={isGuest ? guestIntakePath(intakeAttribution) : undefined}
                        />
                    </div>
                    <Divider orientation="center">or</Divider>
                    <FormItem
                        layout="vertical"
                        label={t('register_page.name_label')}
                        field="name"
                        rules={[{
                            required: true,
                            message: t('register_page.name_placeholder'),
                            validator: (v, cb) => !v || v.trim().length === 0 ? cb(t('register_page.name_placeholder')) : cb()
                        }]}
                    >
                        <Input
                            placeholder={t('register_page.name_placeholder')}
                            autoComplete={"off"}
                        />
                    </FormItem>
                    <FormItem
                        layout="vertical"
                        label={t('register_page.last_name_label')}
                        field="last_name"
                        rules={[{
                            required: true,
                            message: t('register_page.last_name_placeholder'),
                            validator: (v, cb) => !v || v.trim().length === 0 ? cb(t('register_page.last_name_placeholder')) : cb()
                        }]}
                    >
                        <Input
                            placeholder={t('register_page.last_name_placeholder')}
                            autoComplete={"off"}
                        />
                    </FormItem>
                    <FormItem
                        layout="vertical"
                        label={t('register_page.email_label')}
                        field="email"
                        rules={[{required: true, message: t('form_validation_messages.empty_email')}, {
                            validator: (v, cb) => {
                                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                if (!v || v.trim().length === 0) {
                                    cb(t('form_validation_messages.empty_email'));
                                } else if (v.length > 100) {
                                    cb(t('form_validation_messages.long_email'));
                                } else if (!emailRegex.test(v)) {
                                    cb(t('form_validation_messages.invalid_email'));
                                } else {
                                    cb();
                                }
                            },
                        }]}
                    >
                        <Input
                            type="email"
                            placeholder={t('register_page.email_placeholder')}
                            autoComplete={"off"}
                        />
                    </FormItem>
                    <FormItem
                        layout="vertical"
                        label={t('register_page.password_label')}
                        field="password"
                        rules={[{required: true, message: t('form_validation_messages.no_password')}, {
                            minLength: 6,
                            message: t('form_validation_messages.short_password'),
                        },
                            {
                                validator: (v, cb) => {
                                    if (!v || v.trim().length === 0) {
                                        cb(t('form_validation_messages.invalid_password'));
                                    } else {
                                        cb();
                                    }
                                },
                            }]}
                    >
                        <Input.Password
                            type="password"
                            placeholder={t('register_page.password_placeholder')}
                            autoComplete={"off"}
                        />
                    </FormItem>

                    <FormItem layout="vertical">
                        <Row style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 24,
                            marginTop: 10
                        }}>
                            <Button
                                onClick={() => {
                                    form.resetFields();
                                    onGoBack();
                                }}
                                size={"large"}
                                shape={"round"}
                            >
                                {t('register_page.go_back')}
                            </Button>
                            <Button
                                htmlType="submit"
                                style={{
                                    color: getButtonTextColor(),
                                    backgroundColor: getButtonColor()
                                }}
                                size={"large"}
                                shape={"round"}
                            >
                                {t('register_page.create_account')}
                            </Button>
                        </Row>
                    </FormItem>
                </Form>
            </div>
        </div>
    ) : (
        <div
            style={{
                justifyContent: "center",
                backgroundColor: getBoxBackgroundColor(),
                maxWidth: 480,
                width: "calc(100% - 40px)",
                margin: "20px auto",
                padding: "20px",
                borderRadius: 8,
            }}
        >
            <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
                <div style={{
                    display: 'flex',
                    justifyContent: "center",
                    paddingTop: 12,
                    margin: "0 auto",
                }}><LanguageSelect color={getTextColor()}/></div>
            <div style={{ padding: 20, marginTop: 10 }}>
                <Typography.Text
                    style={{ fontSize: 20, color: getColorAccent1(), wordBreak: "break-word" }}
                    type="success"
                    className="alice-regular register-role-intro"
                >
                    {eventIntakeEnabled && !isNycRegistrationMode() ? (
                        <>
                            {t('register_page.role_intro_headline')}{' '}
                            {t('register_page.role_intro_mentor_mentee')}{' '}
                            {t('register_page.role_intro_guest')}
                        </>
                    ) : (
                        t('register_page.start_message')
                    )}
                </Typography.Text>

                {isNycRegistrationMode() && (
                    <Typography.Text
                        style={{
                            display: "block",
                            marginTop: 16,
                            fontSize: 16,
                            fontWeight: "bold",
                            color: "black",
                            wordBreak: "break-word",
                        }}
                    >
                        {t('register_page.advisor_redirect_notice')}
                    </Typography.Text>
                )}

                <div
                    className={`register-role-buttons${
                        isNycRegistrationMode() ? " register-role-buttons--nyc" : ""
                    }${eventIntakeEnabled ? " register-role-buttons--with-guest" : " register-role-buttons--no-guest"}`}
                >
                    <Button
                        className="register-role-button"
                        style={rolePillButtonStyle}
                        onClick={onMentorPick}
                        shape={"round"}
                        size={"large"}
                    >
                        <b>{t('common.mentor')}</b>
                    </Button>
                    <Button
                        className="register-role-button"
                        style={rolePillButtonStyle}
                        onClick={onMenteePick}
                        shape={"round"}
                        size={"large"}
                    >
                        <b>{t('common.mentee')}</b>
                    </Button>
                </div>
                {eventIntakeEnabled ? (
                    <div className="register-role-buttons register-role-buttons--guest-row">
                        <Button
                            className="register-role-button register-role-button--guest"
                            style={rolePillButtonStyle}
                            onClick={onGuestPick}
                            shape={"round"}
                            size={"large"}
                        >
                            <b>{t('register_page.guest_button_label')}</b>
                        </Button>
                    </div>
                ) : null}

                {!isNycRegistrationMode() && (
                <div
                    style={{
                        marginTop: 40,
                        maxWidth: "380px",
                        width: "100%",
                        margin: "60px auto 0",
                    }}
                >
                    <Typography.Text
                        style={{
                            fontSize: 16,
                            color: "black",
                            fontStyle: "italic",
                            fontWeight: "bold",
                            wordBreak: "break-word",
                        }}
                    >
                            {t('register_page.description')}
                    </Typography.Text>
                </div>
                )}
            </div>

            <div style={{ textAlign: "center", marginTop: isNycRegistrationMode() ? 32 : 60 }}>
                <Typography.Paragraph>
                        {t('register_page.account_exists_message')}
                    <NavLink to={loginHref}>
                        <Button
                            style={{ padding: "0 0 0 4px" }}
                            type="text"
                            status="success"
                        >
                            <span style={{ color: getColorAccent1() }}>{t('register_page.log_in_button')}</span>
                        </Button>
                    </NavLink>
                </Typography.Paragraph>
            </div>
        </div>
    );
};
